/**
 * Player state.
 *
 * The split with TanStack Query is deliberate and worth keeping: this store holds
 * *what is playing* (queue, position, volume), the query cache holds *what
 * exists* (library, playlists, search). Neither duplicates the other, so there is
 * no invalidation dance between them.
 */

import { create } from 'zustand'

import type { LibraryItem, RepeatMode } from '@/api/types'

import { engine } from './engine'
import { requestContinuation } from './continuation'

export interface QueueItem {
  /**
   * Identity of this *entry*, not of the track.
   *
   * The same song can legitimately sit in the queue more than once, so
   * libraryItemId does not identify a row. React keys were positional, which
   * meant every reorder remounted the rows below the change — fatal for a drag,
   * because the element being dragged is destroyed mid-gesture and takes its
   * pointer capture with it.
   */
  uid: string
  libraryItemId: number
  trackId: number
  /** The catalog artist, so "more like this" can be offered from the player. */
  localArtistId?: string | null
  artistId: number | null
  version?: string
  explicit?: boolean | null
  isrc?: string | null
  recordingId?: string | null
  title: string
  artistName: string
  albumName: string | null
  coverUrl: string | null
  streamUrl: string
  durationMs: number
  /** An automatically chosen song, distinct from the listener's own queue. */
  suggested?: boolean
  provider?: LibraryItem['track']['provider']
  externalId?: string
}

/** A restorable session, as saved by PlayerProvider and returned by /player/state. */
export interface PlayerSnapshot {
  queue: QueueItem[]
  index: number
  positionMs: number
  shuffle: boolean
  repeat: RepeatMode
  volume: number
  source?: PlaySource
}

let entrySeq = 0

/** Unique per queue entry, for the lifetime of the tab. Never persisted. */
function nextUid(): string {
  entrySeq += 1
  return `q${entrySeq}`
}

/** A deliberate repeat gets its own row identity and is no longer an autoplay suggestion. */
export function duplicateQueueItem(item: QueueItem): QueueItem {
  return { ...item, uid: nextUid(), suggested: false }
}

export function toQueueItem(item: LibraryItem): QueueItem | null {
  if (!item.stream_url) return null
  return {
    uid: nextUid(),
    libraryItemId: item.id,
    trackId: item.track.id,
    provider: item.track.provider,
    externalId: item.track.external_id,
    artistId: item.track.artist_id ?? null,
    localArtistId: item.track.local_artist_id ?? null,
    title: item.track.title,
    version: item.track.version,
    explicit: item.track.explicit,
    isrc: item.track.isrc,
    recordingId: item.track.recording_id,
    artistName: item.track.artist_name,
    albumName: item.track.album_name,
    coverUrl: item.cover_url,
    streamUrl: item.stream_url,
    durationMs: item.track.duration_ms,
  }
}

/** Where a queue came from. Reported with each play, so the history can tell a
 *  song you went looking for from one a shuffle handed you. */
export type PlaySource =
  | 'daylist'
  | 'library'
  | 'album'
  | 'playlist'
  | 'search'
  | 'shuffle'
  | 'queue'
  | 'unknown'

export interface PlayQueueOptions {
  /**
   * Whether `startIndex` is a track the user picked, or merely where the list
   * happens to begin.
   *
   * It only matters with shuffle on. Tapping a song means "play *this*, then
   * surprise me", so that track is pinned to the front. Pressing Shuffle means
   * "surprise me from the start" — and pinning index 0 there is what made every
   * shuffled playlist open with its first track, every time.
   */
  startFrom?: 'chosen' | 'anywhere'
  /** Where the user started this from. A queue really does come from one place,
   *  so it lives on the player rather than on every entry. */
  source?: PlaySource
}

/** A 30 second clip being auditioned, which is not part of the queue. */
export interface PreviewState {
  /** Identity of the search result, so a row can show its own state. */
  key: string
  version?: string
  explicit?: boolean | null
  isrc?: string | null
  recordingId?: string | null
  title: string
  artistName: string
  /** Where playback was when the preview borrowed the audio element. */
  resume: { item: QueueItem; position: number; wasPlaying: boolean } | null
}

interface PlayerState {
  session: number
  intentVersion: number
  awaitingContinuation: boolean
  queue: QueueItem[]
  index: number
  /**
   * Non-null while a preview is playing.
   *
   * A preview is deliberately *not* a queue entry. There is one <audio> element
   * for the whole app, so auditioning something has to borrow it — but the queue,
   * the index, the play count and the saved resume position all have to come
   * through untouched, or listening to a 30 second clip would rewrite what you
   * were actually listening to, on every device.
   */
  preview: PreviewState | null
  /** The pre-shuffle order, kept so turning shuffle off restores it. */
  orderedQueue: QueueItem[] | null
  /** Where the current queue came from; reported with every play. */
  source: PlaySource

  isPlaying: boolean
  isBuffering: boolean
  currentTime: number
  duration: number
  buffered: number

  volume: number
  muted: boolean
  shuffle: boolean
  repeat: RepeatMode
  error: string | null

  current: () => QueueItem | null
  playQueue: (items: QueueItem[], startIndex?: number, options?: PlayQueueOptions) => void
  playAt: (index: number) => void
  toggle: () => void
  play: () => void
  pause: () => void
  next: (auto?: boolean) => void
  previous: () => void
  seek: (seconds: number) => void
  setVolume: (volume: number) => void
  toggleMute: () => void
  toggleShuffle: () => void
  cycleRepeat: () => void
  enqueue: (items: QueueItem[]) => void
  playNextUp: (items: QueueItem[]) => void
  /** Commit a prepared suggestion and advance in one persisted state change. */
  playSuggested: (item: QueueItem) => void
  removeAt: (index: number) => void
  /** Reorder the queue. `to` is the destination in the list once `from` is lifted out. */
  moveInQueue: (from: number, to: number) => void
  /** Audition a clip without adding anything to the queue or the library. */
  startPreview: (preview: { key: string; title: string; artistName: string; url: string }) => void
  /** End the preview and hand the audio element back to the queue. */
  stopPreview: () => void
  clearQueue: () => void
  hydrate: (snapshot: PlayerSnapshot) => void
  setError: (message: string | null) => void
  /** Internal: applied from engine events, not called by components. */
  _sync: (patch: Partial<PlayerState>) => void
}

/**
 * Fisher-Yates, with the track at `keepFirst` pinned to the front.
 *
 * Shuffling *materialises* into the queue rather than living beside it as a
 * permutation of indices. That was the earlier design, and it meant the queue
 * the user was looking at and the order that would actually play were two
 * different things: QueuePanel lists `queue`, so "up next" showed whatever came
 * after the current row, while skip jumped somewhere else entirely and the whole
 * list appeared to reshuffle. It also went stale the moment anything was added
 * to or removed from the queue, because those paths rewrote `queue` and left the
 * index permutation pointing at the old positions.
 *
 * One order, held in one place: what you see is what plays, skip advances by
 * one, and persisting the queue persists the shuffle for free.
 */
function shuffledQueue(items: QueueItem[], keepFirst: number): QueueItem[] {
  const rest = items.filter((_, i) => i !== keepFirst)
  for (let i = rest.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[rest[i], rest[j]] = [rest[j], rest[i]]
  }
  const pinned = keepFirst >= 0 && keepFirst < items.length ? [items[keepFirst]] : []
  return [...pinned, ...rest]
}

export const usePlayer = create<PlayerState>((set, get) => ({
  session: 0,
  intentVersion: 0,
  awaitingContinuation: false,
  queue: [],
  index: -1,
  orderedQueue: null,
  source: 'unknown',
  preview: null,

  isPlaying: false,
  isBuffering: false,
  currentTime: 0,
  duration: 0,
  buffered: 0,

  volume: 1,
  muted: false,
  shuffle: false,
  repeat: 'off',
  error: null,

  current: () => {
    const { queue, index } = get()
    return index >= 0 && index < queue.length ? queue[index] : null
  },

  playQueue: (items, startIndex = 0, options) => {
    if (items.length === 0) return
    const start = Math.max(0, Math.min(startIndex, items.length - 1))

    // With shuffle on, the queue *is* the shuffled order. A track the user
    // actually picked still plays first and everything else reorders behind it;
    // when nobody picked one, nothing is pinned and the whole list is dealt.
    const shuffled = get().shuffle
    const pinned = (options?.startFrom ?? 'chosen') === 'chosen'
    const queue = shuffled ? shuffledQueue(items, pinned ? start : -1) : items
    const index = shuffled ? 0 : start

    set({
      queue,
      session: get().session + 1,
      intentVersion: get().intentVersion + 1,
      awaitingContinuation: false,
      preview: null,
      orderedQueue: shuffled ? items : null,
      source: options?.source ?? 'unknown',
      index,
      error: null,
      currentTime: 0,
    })
    startTrack(queue[index])
  },

  playAt: (index) => {
    const { queue } = get()
    if (index < 0 || index >= queue.length) return
    set({ index, currentTime: 0, error: null, preview: null, intentVersion: get().intentVersion + 1, awaitingContinuation: false })
    startTrack(queue[index])
  },

  play: () => {
    set({ intentVersion: get().intentVersion + 1, awaitingContinuation: false })
    const item = get().current()
    if (!item) return
    void engine.play().catch(() => set({ isPlaying: false }))
  },

  pause: () => {
    set({ intentVersion: get().intentVersion + 1, awaitingContinuation: false })
    engine.pause()
  },

  toggle: () => {
    const { isPlaying, current } = get()
    if (!current()) return
    if (isPlaying || get().awaitingContinuation) get().pause()
    else get().play()
  },

  /**
   * `auto` distinguishes a track ending from the user pressing skip. Only the
   * automatic case honours "repeat one", because a user pressing next while
   * repeat-one is on clearly wants the *next* track.
   */
  next: (auto = false) => {
    set({ intentVersion: get().intentVersion + 1, awaitingContinuation: false })
    const { queue, index, repeat } = get()
    if (queue.length === 0) return

    if (auto && repeat === 'one') {
      engine.seek(0)
      void engine.play()
      return
    }

    // Straight down the queue, shuffled or not. Skipping and a track ending take
    // exactly the same path, which is the whole point: the next song is the one
    // the queue says is next.
    const atEnd = index >= queue.length - 1

    if (atEnd && repeat !== 'all') {
      if (repeat === 'off' && requestContinuation()) {
        engine.pause()
        set({ isPlaying: false })
        return
      }
      if (auto) {
        engine.pause()
        engine.seek(0)
        set({ isPlaying: false, currentTime: 0 })
      }
      return
    }

    const nextIndex = (index + 1) % queue.length
    set({ index: nextIndex, currentTime: 0, error: null })
    startTrack(queue[nextIndex])
  },

  previous: () => {
    set({ intentVersion: get().intentVersion + 1, awaitingContinuation: false })
    const { queue, index } = get()
    if (queue.length === 0) return

    // Standard music-player behaviour: restart the track unless we are within
    // the first few seconds, in which case go back one.
    if (engine.currentTime > 3) {
      engine.seek(0)
      return
    }

    const previousIndex = (index - 1 + queue.length) % queue.length
    set({ index: previousIndex, currentTime: 0, error: null })
    startTrack(queue[previousIndex])
  },

  seek: (seconds) => {
    set({ intentVersion: get().intentVersion + 1, awaitingContinuation: false })
    engine.seek(seconds)
    set({ currentTime: seconds })
  },

  setVolume: (volume) => {
    const clamped = Math.max(0, Math.min(1, volume))
    engine.setVolume(clamped)
    engine.setMuted(false)
    set({ volume: clamped, muted: false })
  },

  toggleMute: () => {
    const muted = !get().muted
    engine.setMuted(muted)
    set({ muted })
  },

  toggleShuffle: () => {
    const { shuffle, queue, index, orderedQueue } = get()
    const turningOn = !shuffle
    const playing = index >= 0 && index < queue.length ? queue[index] : null

    if (queue.length === 0) {
      set({ shuffle: turningOn, orderedQueue: null })
      return
    }

    if (turningOn) {
      // Reorder in place. Whatever is playing keeps playing and moves to the
      // front, so nothing restarts.
      set({
        shuffle: true,
        orderedQueue: queue,
        queue: shuffledQueue(queue, index),
        index: playing ? 0 : index,
      })
      return
    }

    // Turning it off puts the original order back and lands on the track still
    // playing. If the saved order no longer holds it -- the queue was edited
    // while shuffled -- keep what is on screen rather than jumping somewhere.
    const restored = orderedQueue ?? queue
    const landing = playing
      ? restored.findIndex((item) => item.uid === playing.uid)
      : -1
    if (landing < 0) {
      set({ shuffle: false, orderedQueue: null })
      return
    }
    set({ shuffle: false, orderedQueue: null, queue: restored, index: landing })
  },

  cycleRepeat: () => {
    const order: RepeatMode[] = ['off', 'all', 'one']
    const current = order.indexOf(get().repeat)
    set({ repeat: order[(current + 1) % order.length] })
  },

  enqueue: (items) => {
    if (items.length === 0) return
    const { queue, index } = get()
    const combined = [...queue, ...items]
    // An empty queue means this is really a "play now".
    if (index === -1) {
      get().playQueue(combined, 0, { source: 'queue' })
      return
    }
    const ordered = get().orderedQueue
    set({ queue: combined, orderedQueue: ordered ? [...ordered, ...items] : null })
  },

  /** Insert directly after the current track — the "play next" affordance. */
  playNextUp: (items) => {
    if (items.length === 0) return
    const { queue, index } = get()
    if (index === -1) {
      get().playQueue(items, 0, { source: 'queue' })
      return
    }
    const combined = [...queue.slice(0, index + 1), ...items, ...queue.slice(index + 1)]
    const ordered = get().orderedQueue
    set({ queue: combined, orderedQueue: ordered ? [...ordered, ...items] : null })
  },

  playSuggested: (item) => {
    const { queue, index, orderedQueue } = get()
    // Bound played history during endless listening, without discarding any
    // manually queued songs that have not yet had their turn.
    const trim = index === queue.length - 1 && queue.length >= 200
    const history = trim ? queue.slice(-199) : queue
    const suggestion = { ...item, suggested: true }
    const combined = [...history, suggestion]
    set({
      queue: combined,
      index: combined.length - 1,
      orderedQueue: !trim && orderedQueue ? [...orderedQueue, suggestion] : null,
      currentTime: 0,
      error: null,
      preview: null,
      intentVersion: get().intentVersion + 1,
      awaitingContinuation: false,
    })
    startTrack(suggestion)
  },

  startPreview: ({ key, title, artistName, url }) => {
    set({ intentVersion: get().intentVersion + 1, awaitingContinuation: false })
    const { queue, index, isPlaying, currentTime, preview } = get()
    // Remember where real playback was, but only on the first preview — hopping
    // between clips must not overwrite the resume point with another preview.
    const resume =
      preview?.resume ??
      (index >= 0 && index < queue.length
        ? { item: queue[index], position: currentTime, wasPlaying: isPlaying }
        : null)

    set({ preview: { key, title, artistName, resume }, error: null, currentTime: 0 })
    engine.load(url)
    void engine.play()
  },

  stopPreview: () => {
    const { preview } = get()
    if (!preview) return
    set({ preview: null, currentTime: 0 })

    const resume = preview.resume
    if (!resume) {
      // Nothing was playing before, so leave the element idle rather than
      // starting something the user never asked for.
      engine.pause()
      engine.reset()
      set({ isPlaying: false, duration: 0 })
      return
    }

    // Put the track back exactly where it was. Seeking has to wait for the
    // metadata, the same way hydrate() does.
    engine.load(resume.item.streamUrl)
    const unsubscribe = engine.subscribe((event) => {
      if (event.type === 'durationchange' && event.duration > 0) {
        engine.seek(resume.position)
        if (resume.wasPlaying) void engine.play()
        unsubscribe()
      }
    })
  },

  moveInQueue: (from, to) => {
    const { queue, index } = get()
    if (queue.length === 0) return
    if (from < 0 || from >= queue.length) return
    if (to < 0 || to >= queue.length) return
    if (from === to) return

    const next = [...queue]
    const [moved] = next.splice(from, 1)
    next.splice(to, 0, moved)

    // Follow the *track*, not the number. This is the only action that rewrites
    // the queue while audio is playing, so getting it wrong does not shuffle a
    // list — it silently makes the player think a different song is on.
    let landing = index
    if (from === index) {
      landing = to
    } else {
      if (from < index) landing -= 1
      if (to <= landing) landing += 1
    }

    // Deliberately absent: startTrack, engine calls and any currentTime reset.
    // Rearranging what comes later must not interrupt what is playing now.
    // orderedQueue is dropped because the user has just expressed an order of
    // their own; restoring a pre-shuffle one afterwards would throw it away.
    set({ queue: next, orderedQueue: null, index: landing })
  },

  removeAt: (target) => {
    const { queue, index, orderedQueue } = get()
    if (target < 0 || target >= queue.length) return
    const dropped = queue[target]
    const combined = queue.filter((_, i) => i !== target)
    // Drop it from the pre-shuffle order too, or turning shuffle off would
    // resurrect a track the user removed.
    const ordered =
      orderedQueue?.filter((item) => item.uid !== dropped.uid) ?? null

    if (combined.length === 0) {
      engine.pause()
      engine.reset()
      set({
        queue: [],
        session: get().session + 1,
        intentVersion: get().intentVersion + 1,
        awaitingContinuation: false,
        orderedQueue: null,
        index: -1,
        isPlaying: false,
        currentTime: 0,
        duration: 0,
      })
      return
    }

    if (target < index) {
      set({ queue: combined, orderedQueue: ordered, index: index - 1 })
    } else if (target === index) {
      // Removing what is playing advances to whatever took its place.
      const nextIndex = Math.min(index, combined.length - 1)
      set({ queue: combined, orderedQueue: ordered, index: nextIndex, currentTime: 0, intentVersion: get().intentVersion + 1, awaitingContinuation: false })
      startTrack(combined[nextIndex])
    } else {
      set({ queue: combined, orderedQueue: ordered })
    }
  },

  clearQueue: () => {
    engine.pause()
    engine.reset()
    set({
      queue: [],
      session: get().session + 1,
      intentVersion: get().intentVersion + 1,
      awaitingContinuation: false,
      preview: null,
      orderedQueue: null,
      index: -1,
      isPlaying: false,
      currentTime: 0,
      duration: 0,
      buffered: 0,
    })
  },

  /**
   * Restore a saved session. Loads the track and seeks to where the user left
   * off, but deliberately stays **paused**: browsers block audio that starts
   * without a gesture, and a page that begins blaring on load would be hostile
   * even if they allowed it.
   */
  hydrate: (snapshot) => {
    if (snapshot.queue.length === 0) return
    const index = Math.max(0, Math.min(snapshot.index, snapshot.queue.length - 1))

    engine.setVolume(snapshot.volume)
    set({
      queue: snapshot.queue,
      session: get().session + 1,
      intentVersion: get().intentVersion + 1,
      awaitingContinuation: false,
      index,
      shuffle: snapshot.shuffle,
      repeat: snapshot.repeat,
      volume: snapshot.volume,
      source: snapshot.source ?? 'unknown',
      currentTime: snapshot.positionMs / 1000,
      isPlaying: false,
      // No re-shuffle on restore. The saved queue is already the shuffled order,
      // so resuming continues where it left off instead of dealing a new hand.
      // The pre-shuffle order is not persisted, so turning shuffle off after a
      // reload keeps the current order rather than restoring one we never saw.
      orderedQueue: null,
    })

    engine.load(snapshot.queue[index].streamUrl)
    if (snapshot.positionMs > 0) {
      // Seeking before metadata arrives is ignored, so wait for the duration.
      const unsubscribe = engine.subscribe((event) => {
        if (event.type === 'durationchange' && event.duration > 0) {
          engine.seek(snapshot.positionMs / 1000)
          unsubscribe()
        }
      })
    }
  },

  setError: (message) => set({ error: message }),

  _sync: (patch) => set(patch as PlayerState),
}))

function startTrack(item: QueueItem): void {
  engine.load(item.streamUrl)
  void engine.play().catch(() => usePlayer.setState({ isPlaying: false }))
}
